/**
 * Route visibility and navigation for Regional Branch vs team members.
 * Team members are identified by both TeamInfo and TeamRoleAccess on the on-load payload.
 */

export function normalizeRoutePath(path) {
  if (path == null || path === '') return '/';
  const p = String(path).trim().toLowerCase();
  if (!p.startsWith('/')) return `/${p}`;
  return p;
}

/** True when the session is a team member subject to routeAccess (not a regional manager). */
export function isTeamMemberWithRouteRestrictions(regBranchSession) {
  if (!regBranchSession) return false;
  return !!(regBranchSession.teamInfo && regBranchSession.teamRoleAccess);
}

/** Only regional branch managers may open these paths (never team members). */
export const REGIONAL_MANAGER_ONLY_ROUTES = [
  '/teams',
  '/squads',
  '/analytics',
  '/servermonitoring',
  '/notifications',
];

export function isRegionalManagerOnlyRoute(pathname) {
  const pathNorm = normalizeRoutePath(pathname);
  return REGIONAL_MANAGER_ONLY_ROUTES.some(
    (route) => pathNorm === route || pathNorm.startsWith(`${route}/`)
  );
}

/** Team member must verify phone (OnLoad `TeamInfo.phone_verified_status` not true). */
export function needsTeamPhoneVerification(regBranchSession) {
  const t = regBranchSession?.teamInfo;
  if (!t || typeof t !== 'object') return false;
  return t.phone_verified_status !== true;
}

function activeRouteAccessEntries(regBranchSession) {
  const list = regBranchSession?.teamRoleAccess?.routeAccess;
  if (!Array.isArray(list)) return [];
  return list.filter((entry) => {
    if (entry == null || entry.is_active === false) return false;
    if (entry.access && entry.access.read === false) return false;
    return true;
  });
}

/** Set of allowed path prefixes, or null when user is not route-restricted (regional manager). */
export function getAllowedRoutePrefixSet(regBranchSession) {
  if (!isTeamMemberWithRouteRestrictions(regBranchSession)) return null;
  const set = new Set();
  for (const entry of activeRouteAccessEntries(regBranchSession)) {
    const r = normalizeRoutePath(entry.route);
    if (r && r !== '/') set.add(r);
  }
  return set;
}

/**
 * Dashboard (/) is always allowed. Regional managers may access any route.
 * Team members may only access paths matching an entry in routeAccess (prefix match for nested paths).
 */
export function canAccessRoute(pathname, regBranchSession) {
  const pathNorm = normalizeRoutePath(pathname);
  if (pathNorm === '/') return true;
  if (pathNorm === '/phone-verify' && needsTeamPhoneVerification(regBranchSession)) return true;
  if (
    isTeamMemberWithRouteRestrictions(regBranchSession) &&
    isRegionalManagerOnlyRoute(pathname)
  ) {
    return false;
  }
  if (!isTeamMemberWithRouteRestrictions(regBranchSession)) return true;
  const allowed = getAllowedRoutePrefixSet(regBranchSession);
  if (!allowed || allowed.size === 0) return false;
  for (const route of allowed) {
    if (pathNorm === route || pathNorm.startsWith(`${route}/`)) return true;
  }
  return false;
}
